const labelCollator = new Intl.Collator("tr", { numeric: true });
export type ProductAnalysisRow = {
  label: string; category: string; season: string;
  subcategory?: string; color_name?: string;
  import_units: number; first_import: string | null; last_import: string | null;
  sales_units: number; stock_units: number; sellthrough_percent: number | null;
  price_uzs: number; average_unit_price_uzs: number; discount_percent: number | null;
  daily_sales: number; stock_days: number | null; first_sale: string | null;
  last_sale: string | null; days_without_sale: number | null; status: string;
  total_rows: number; overall_import_units: number; overall_sales_units: number;
  overall_stock_units: number; overall_revenue_uzs: number; overall_gross_uzs: number;
  overall_discount_revenue_uzs?: number; overall_discount_reference_uzs?: number;
};
export type ProductAnalysisSort = { key: string; direction: "asc" | "desc" } | null;
export type ProductAnalysisPage = { rows: ProductAnalysisRow[]; total_rows: number };
export type ProductVariant = {
  color_code: string; color_name: string; size: string; barcodes: string[];
  import_units: number; sales_units: number; stock_units: number;
};
export type ProductColorImportDates = { color_code: string; first_import: string | null; last_import: string | null };
export type ProductVariantDetail = {
  release_id: string; sku: string; period: { start: string; end: string };
  variants: ProductVariant[]; inventory_scope_supported: boolean;
  color_import_dates?: ProductColorImportDates[]; import_dates_unavailable?: boolean;
};
export type ProductVariantAggregate = {
  label: string; color_name: string; barcodes: string[];
  import_units: number; sales_units: number; stock_units: number;
};

export function parseProductSkuList(value: string): string[] {
  // Excel columns use line/tab boundaries. Spaces can belong to a single SKU.
  return Array.from(new Set(value.split(/[\r\n\t,;]+/).map(sku => sku.trim()).filter(Boolean)));
}

export function productExportBlocked(loading: boolean, tableBusy: boolean, exporting: boolean, hasReport: boolean): boolean {
  return loading || tableBusy || exporting || !hasReport;
}

export function productSellthrough(sales: number, stock: number): number | null {
  return sales + stock === 0 ? null : sales / (sales + stock) * 100;
}

export function aggregateProductVariants(
  rows: ProductVariant[], dimension: "color" | "size", colorCode = "",
): ProductVariantAggregate[] {
  const groups = new Map<string, ProductVariantAggregate>();
  for (const row of rows) {
    if (colorCode && row.color_code !== colorCode) continue;
    const label = dimension === "color" ? row.color_code : row.size;
    const current = groups.get(label) ?? {
      label, color_name: dimension === "color" ? row.color_name : "", barcodes: [],
      import_units: 0, sales_units: 0, stock_units: 0,
    };
    current.import_units += row.import_units;
    current.sales_units += row.sales_units;
    current.stock_units += row.stock_units;
    current.barcodes = Array.from(new Set([...current.barcodes, ...row.barcodes])).sort();
    groups.set(label, current);
  }
  return [...groups.values()].sort((a, b) => dimension === "size"
    ? labelCollator.compare(a.label, b.label)
    : b.sales_units - a.sales_units || labelCollator.compare(a.label, b.label));
}

export function productVariantTotals(rows: ProductVariantAggregate[]) {
  return rows.reduce((t, row) => ({
    import_units: t.import_units + row.import_units,
    sales_units: t.sales_units + row.sales_units,
    stock_units: t.stock_units + row.stock_units,
  }), { import_units: 0, sales_units: 0, stock_units: 0 });
}
